// Considered: None — overlay editing is a feature-specific surface. Composes DrawerAtom + existing primitives; no existing organism covers this pattern. See spec Reuse Audit.
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import DrawerAtom from '../../atoms/Drawer/DrawerAtom';
import ForkMetaspacePromptMolecule from '../../molecules/ForkMetaspacePrompt/ForkMetaspacePromptMolecule';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import './MetaspaceOverlayEditorOrganism.scss';

export interface ToolbarItemConfig {
  id: string;
  label: string;
  icon?: string;
}

export interface LayoutPanelConfig {
  id: string;
  title?: string;
  type: string;
}

export interface MetaspaceOverlayEditorOrganismProps {
  open: boolean;
  sphereId: string;
  deploymentId: string;
  metaspaceName: string;
  toolbarItems?: ToolbarItemConfig[];
  layoutPanels?: LayoutPanelConfig[];
  currentOverrides?: {
    disabledToolbarItems?: string[];
    toolbarOrder?: string[];
    layoutOrder?: string[];
    settings?: {
      theme?: string;
      columns?: number;
      width?: number;
    };
  };
  onClose: () => void;
  onSaved: () => void;
}

interface OverlayEdits {
  disabledToolbarItems: string[];
  toolbarOrder: string[];
  layoutOrder: string[];
  theme: string;
  columns: number;
}

const MetaspaceOverlayEditorOrganism: React.FC<MetaspaceOverlayEditorOrganismProps> = ({
  open,
  sphereId,
  deploymentId,
  metaspaceName,
  toolbarItems = [],
  layoutPanels = [],
  currentOverrides = {},
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const initEdits = useCallback((): OverlayEdits => ({
    disabledToolbarItems: currentOverrides.disabledToolbarItems ?? [],
    toolbarOrder: currentOverrides.toolbarOrder ?? toolbarItems.map((i) => i.id),
    layoutOrder: currentOverrides.layoutOrder ?? layoutPanels.map((p) => p.id),
    theme: currentOverrides.settings?.theme ?? 'light',
    columns: currentOverrides.settings?.columns ?? 12,
  }), [currentOverrides, toolbarItems, layoutPanels]);

  const [edits, setEdits] = useState<OverlayEdits>(initEdits);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForkPrompt, setShowForkPrompt] = useState(false);
  const [forking, setForking] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEdits(initEdits());
      setError(null);
      setForkError(null);
      setShowForkPrompt(false);
    }
  }, [open, initEdits]);

  const toggleToolbarItem = useCallback((id: string) => {
    setEdits((prev) => ({
      ...prev,
      disabledToolbarItems: prev.disabledToolbarItems.includes(id)
        ? prev.disabledToolbarItems.filter((x) => x !== id)
        : [...prev.disabledToolbarItems, id],
    }));
  }, []);

  const moveToolbarItem = useCallback((id: string, direction: 'up' | 'down') => {
    setEdits((prev) => {
      const order = [...prev.toolbarOrder];
      const idx = order.indexOf(id);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= order.length) return prev;
      [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
      return { ...prev, toolbarOrder: order };
    });
  }, []);

  const moveLayoutPanel = useCallback((id: string, direction: 'up' | 'down') => {
    setEdits((prev) => {
      const order = [...prev.layoutOrder];
      const idx = order.indexOf(id);
      if (idx === -1) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= order.length) return prev;
      [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
      return { ...prev, layoutOrder: order };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!apiClients?.graphClient) return;

    setSaving(true);
    setError(null);

    try {
      await apiClients.graphClient.patch(
        `/spheres/${encodeURIComponent(sphereId)}/metaspaces/${encodeURIComponent(deploymentId)}`,
        {
          overrides: {
            disabledToolbarItems: edits.disabledToolbarItems,
            toolbarOrder: edits.toolbarOrder,
            layoutOrder: edits.layoutOrder,
            settings: {
              theme: edits.theme,
              columns: edits.columns,
            },
          },
        }
      );
      onSaved();
      onClose();
    } catch {
      setError(t('sphereMetaspaces.overlay.saveError'));
    } finally {
      setSaving(false);
    }
  }, [apiClients, sphereId, deploymentId, edits, onSaved, onClose, t]);

  const handleResetToTemplate = useCallback(() => {
    setEdits(initEdits());
  }, [initEdits]);

  const handleForkConfirm = useCallback(async () => {
    if (!apiClients?.graphClient) return;

    setForking(true);
    setForkError(null);

    try {
      await apiClients.graphClient.post(
        `/spheres/${encodeURIComponent(sphereId)}/metaspaces/${encodeURIComponent(deploymentId)}/fork`,
        { name: metaspaceName }
      );
      setShowForkPrompt(false);
      onSaved();
      onClose();
    } catch {
      setForkError(t('sphereMetaspaces.fork.error'));
    } finally {
      setForking(false);
    }
  }, [apiClients, sphereId, deploymentId, metaspaceName, onSaved, onClose, t]);

  const orderedToolbarItems = edits.toolbarOrder
    .map((id) => toolbarItems.find((item) => item.id === id))
    .filter(Boolean) as ToolbarItemConfig[];

  const unorderedToolbarItems = toolbarItems.filter(
    (item) => !edits.toolbarOrder.includes(item.id)
  );

  const allOrderedToolbarItems = [...orderedToolbarItems, ...unorderedToolbarItems];

  const orderedLayoutPanels = edits.layoutOrder
    .map((id) => layoutPanels.find((p) => p.id === id))
    .filter(Boolean) as LayoutPanelConfig[];

  const unorderedPanels = layoutPanels.filter(
    (p) => !edits.layoutOrder.includes(p.id)
  );

  const allOrderedPanels = [...orderedLayoutPanels, ...unorderedPanels];

  const drawerHeader = (
    <span className="metaspace-overlay-editor__title">
      {t('sphereMetaspaces.overlay.title')}
      {metaspaceName && (
        <span className="metaspace-overlay-editor__metaspace-name"> — {metaspaceName}</span>
      )}
    </span>
  );

  return (
    <>
      <DrawerAtom
        visible={open}
        onHide={onClose}
        position="right"
        width="380px"
        header={drawerHeader}
        className="metaspace-overlay-editor"
      >
        <div className="metaspace-overlay-editor__content">
          {allOrderedToolbarItems.length > 0 && (
            <section className="metaspace-overlay-editor__section">
              <h3 className="metaspace-overlay-editor__section-title">
                {t('sphereMetaspaces.overlay.toolbarSection')}
              </h3>
              <ul className="metaspace-overlay-editor__list">
                {allOrderedToolbarItems.map((item, idx) => {
                  const isDisabled = edits.disabledToolbarItems.includes(item.id);
                  return (
                    <li key={item.id} className="metaspace-overlay-editor__list-item">
                      <button
                        type="button"
                        className={`metaspace-overlay-editor__visibility-btn${isDisabled ? ' metaspace-overlay-editor__visibility-btn--hidden' : ''}`}
                        onClick={() => toggleToolbarItem(item.id)}
                        aria-label={isDisabled
                          ? t('sphereMetaspaces.overlay.showItem')
                          : t('sphereMetaspaces.overlay.hideItem')}
                        title={isDisabled
                          ? t('sphereMetaspaces.overlay.showItem')
                          : t('sphereMetaspaces.overlay.hideItem')}
                      >
                        <i className={isDisabled ? 'pi pi-eye-slash' : 'pi pi-eye'} />
                      </button>
                      <span className={`metaspace-overlay-editor__item-label${isDisabled ? ' metaspace-overlay-editor__item-label--disabled' : ''}`}>
                        {item.icon && <i className={item.icon} />}
                        {item.label}
                      </span>
                      <div className="metaspace-overlay-editor__order-btns">
                        <button
                          type="button"
                          className="metaspace-overlay-editor__order-btn"
                          onClick={() => moveToolbarItem(item.id, 'up')}
                          disabled={idx === 0}
                          aria-label={t('sphereMetaspaces.overlay.moveUp')}
                          title={t('sphereMetaspaces.overlay.moveUp')}
                        >
                          <i className="pi pi-chevron-up" />
                        </button>
                        <button
                          type="button"
                          className="metaspace-overlay-editor__order-btn"
                          onClick={() => moveToolbarItem(item.id, 'down')}
                          disabled={idx === allOrderedToolbarItems.length - 1}
                          aria-label={t('sphereMetaspaces.overlay.moveDown')}
                          title={t('sphereMetaspaces.overlay.moveDown')}
                        >
                          <i className="pi pi-chevron-down" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {allOrderedPanels.length > 0 && (
            <section className="metaspace-overlay-editor__section">
              <h3 className="metaspace-overlay-editor__section-title">
                {t('sphereMetaspaces.overlay.layoutSection')}
              </h3>
              <ul className="metaspace-overlay-editor__list">
                {allOrderedPanels.map((panel, idx) => (
                  <li key={panel.id} className="metaspace-overlay-editor__list-item">
                    <i className="pi pi-th-large metaspace-overlay-editor__panel-icon" />
                    <span className="metaspace-overlay-editor__item-label">
                      {panel.title ?? panel.type}
                    </span>
                    <div className="metaspace-overlay-editor__order-btns">
                      <button
                        type="button"
                        className="metaspace-overlay-editor__order-btn"
                        onClick={() => moveLayoutPanel(panel.id, 'up')}
                        disabled={idx === 0}
                        aria-label={t('sphereMetaspaces.overlay.moveUp')}
                        title={t('sphereMetaspaces.overlay.moveUp')}
                      >
                        <i className="pi pi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        className="metaspace-overlay-editor__order-btn"
                        onClick={() => moveLayoutPanel(panel.id, 'down')}
                        disabled={idx === allOrderedPanels.length - 1}
                        aria-label={t('sphereMetaspaces.overlay.moveDown')}
                        title={t('sphereMetaspaces.overlay.moveDown')}
                      >
                        <i className="pi pi-chevron-down" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="metaspace-overlay-editor__section">
            <h3 className="metaspace-overlay-editor__section-title">
              {t('sphereMetaspaces.overlay.appearanceSection')}
            </h3>

            <div className="metaspace-overlay-editor__field">
              <label className="metaspace-overlay-editor__label">
                {t('sphereMetaspaces.overlay.theme')}
              </label>
              <div className="metaspace-overlay-editor__toggle-group">
                <button
                  type="button"
                  className={`metaspace-overlay-editor__toggle-btn${edits.theme === 'light' ? ' metaspace-overlay-editor__toggle-btn--active' : ''}`}
                  onClick={() => setEdits((prev) => ({ ...prev, theme: 'light' }))}
                >
                  {t('sphereMetaspaces.overlay.themeLight')}
                </button>
                <button
                  type="button"
                  className={`metaspace-overlay-editor__toggle-btn${edits.theme === 'dark' ? ' metaspace-overlay-editor__toggle-btn--active' : ''}`}
                  onClick={() => setEdits((prev) => ({ ...prev, theme: 'dark' }))}
                >
                  {t('sphereMetaspaces.overlay.themeDark')}
                </button>
              </div>
            </div>

            <div className="metaspace-overlay-editor__field">
              <label className="metaspace-overlay-editor__label">
                {t('sphereMetaspaces.overlay.columns')}
              </label>
              <input
                type="number"
                min={1}
                max={24}
                value={edits.columns}
                onChange={(e) =>
                  setEdits((prev) => ({ ...prev, columns: Number(e.target.value) }))
                }
                className="metaspace-overlay-editor__number-input"
              />
            </div>
          </section>

          <section className="metaspace-overlay-editor__section metaspace-overlay-editor__section--fork">
            <h3 className="metaspace-overlay-editor__section-title">
              {t('sphereMetaspaces.overlay.forkSection')}
            </h3>
            <p className="metaspace-overlay-editor__fork-desc">
              {t('sphereMetaspaces.overlay.forkSectionDesc')}
            </p>
            <button
              type="button"
              className="metaspace-overlay-editor__fork-btn"
              onClick={() => setShowForkPrompt(true)}
            >
              <i className="pi pi-code-branch" />
              {t('sphereMetaspaces.overlay.forkAction')}
            </button>
            {forkError && (
              <div className="metaspace-overlay-editor__error">{forkError}</div>
            )}
          </section>

          {error && (
            <div className="metaspace-overlay-editor__error">{error}</div>
          )}

          <div className="metaspace-overlay-editor__footer">
            <button
              type="button"
              className="metaspace-overlay-editor__reset-btn"
              onClick={handleResetToTemplate}
              disabled={saving}
            >
              {t('sphereMetaspaces.overlay.resetToTemplate')}
            </button>
            <button
              type="button"
              className="metaspace-overlay-editor__save-btn"
              onClick={handleSave}
              disabled={saving || !apiClients}
            >
              {saving
                ? t('sphereMetaspaces.overlay.saving')
                : t('sphereMetaspaces.overlay.save')}
            </button>
          </div>
        </div>
      </DrawerAtom>

      <ForkMetaspacePromptMolecule
        open={showForkPrompt}
        onConfirm={handleForkConfirm}
        onCancel={() => {
          setShowForkPrompt(false);
          setForkError(null);
        }}
        loading={forking}
      />
    </>
  );
};

MetaspaceOverlayEditorOrganism.displayName = 'MetaspaceOverlayEditorOrganism';

export default MetaspaceOverlayEditorOrganism;
